import classNames from 'classnames';
import { useRouter } from 'next/router';

import { CoreGlobalNavigationFooterData } from 'src/types/components';

import ComplianceNav from 'components/BaseHelpers/Footer/ComplianceNav';
import FooterCard from 'components/BaseHelpers/Footer/FooterCard';
import PrimaryNav from 'components/BaseHelpers/Footer/PrimaryNav';
import SocialLinks from 'components/BaseHelpers/Footer/SocialLinks';
import Image from 'components/BaseHelpers/Image';
import Link from 'components/BaseHelpers/Link';
import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';

import styles from './CoreNavigationFooter.module.scss';

export type CoreNavigationFooterProps = CoreGlobalNavigationFooterData;

const CoreNavigationFooter = ({ fields }: CoreNavigationFooterProps) => {
  const router = useRouter();
  const isHomePage = router.asPath === '/';

  const updatedLogo = isHomePage
    ? {
        src: '/images/SLUHN_white.svg',
        alt: 'St Lukes University Health Network',
      }
    : fields.footerLogo;

  const updatedPrimaryNav: typeof fields.primaryNav = isHomePage
    ? [
        {
          label: 'College of Health Sciences',
          href: '/college',
        },
        {
          label: 'School of Medicine',
          target: '_blank',
          href: 'https://www.slhn.org/school-of-medicine',
        },
        {
          label: 'School of Nursing',
          target: '_blank',
          href: 'https://www.slhn.org/school-of-nursing',
        },
        {
          label: 'Graduate Medical Education',
          target: '_blank',
          href: 'https://www.slhn.org/graduate-medical-education',
        },
        {
          label: 'Additional Programs',
          target: '_blank',
          href: 'https://www.slhn.org/med-ed/programs-resources',
        },
      ]
    : fields.primaryNav;

  const updatedMessageCard =
    isHomePage && fields.messageCard
      ? {
          ...fields.messageCard,
          header: 'Careers',
          description: `<p>St Luke’s remarkable growth is the result of the trust we’ve earned from the communities we have served over the course of our over 150-year history. This growth has provided the opportunity to welcome more employees, attracted by competitive salaries and benefits, into our St. Luke’s family.</p><p>Interested in joining an award-winning organization that ALWAYS puts its employees first?</p>`,
          primaryCallToAction: fields.messageCard.primaryCallToAction
            ? {
                label: 'Search Careers',
                target: '_blank',
                href: 'https://www.slhn.org/careers',
              }
            : fields.messageCard.primaryCallToAction,
          secondaryCallToAction: null,
        }
      : fields.messageCard;

  const updatedComplianceLinks = isHomePage
    ? [
        {
          label: 'Non-Discrimination Policy',
          href: '/academics/academic-policies-and-procedures/students-with-disabilities-non-discrimination-policies/',
        },
        {
          label: 'Media Relations',
          href: '/media-relations/',
        },
      ]
    : fields.complianceLinks;

  const updatedCopyright = isHomePage
    ? `© Copyright 2025 St. Luke's University Health Network, All rights reserved.`
    : fields.copyright;

  return (
    <footer className={styles.main}>
      <div className={classNames(styles.innerWrapper, 'container-large')}>
        <div className={styles['column--left']}>
          <div className={styles.innerColumn}>
            {updatedLogo && (
              <Link
                href="/"
                className={classNames(styles.logoLink, {
                  [styles.logoLinkHomePage]: isHomePage,
                })}
                aria-label="Navigate to the homepage"
              >
                <Image
                  aria-label="StLukes college of health sciences"
                  className={styles.logo}
                  {...updatedLogo}
                  fill
                />
              </Link>
            )}
            <RichText className={styles.subtitle} field={fields.location} />
          </div>
          <nav
            className={classNames(styles.innerColumn, styles.leftHandNavs)}
            aria-label="Footer Navigation"
          >
            <PrimaryNav navigationItems={updatedPrimaryNav} className={styles.primaryNav} />
            {!isHomePage && (
              <SocialLinks links={fields.socialMediaLinks} className={styles.socials} />
            )}
          </nav>
        </div>
        <div className={styles['column--right']}>
          <FooterCard className={styles.card} messageCard={updatedMessageCard} />
          <div className={styles.rightColumnBottomSection}>
            <ComplianceNav navigationItems={updatedComplianceLinks} />
            <div className={styles.noticeWrapper}>
              {!!updatedCopyright && (
                <p className={styles.noticeCopy}>
                  <Text field={updatedCopyright} />
                </p>
              )}
              <RichText
                className={styles.noticeCopy}
                styleType="sub-text"
                field={fields.givingDisclaimer}
              />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default CoreNavigationFooter;
